import React, { useEffect, useMemo, useState } from "react";
import { useAdminAuth } from "./AuthContext";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Phone,
  Mail,
  MapPin,
  CalendarDays,
  Loader2,
  Search,
  User,
  FileText,
  Image as ImageIcon,
  Pencil,
  Trash2,
  Plus,
} from "lucide-react";
import BookingEditModal from "./BookingEditModal";
import BookingActionModal from "./BookingActionModal";
import CustomerEditModal from "./CustomerEditModal";
import JobCreateModal from "./JobCreateModal";
import { useToast } from "../hooks/use-toast";

const STATUS_STYLES = {
  requested: "bg-amber-100 text-amber-800",
  confirmed: "bg-emerald-100 text-emerald-800",
  declined: "bg-rose-100 text-rose-800",
  completed: "bg-slate-200 text-slate-700",
  cancelled: "bg-slate-200 text-slate-500 line-through",
};

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

export default function AdminCustomers() {
  const { authAxios } = useAdminAuth();
  const { toast } = useToast();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [editJob, setEditJob] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [completeJob, setCompleteJob] = useState(null);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [customerEditOpen, setCustomerEditOpen] = useState(false);
  const [customerDeleteOpen, setCustomerDeleteOpen] = useState(false);
  const [customerDeleting, setCustomerDeleting] = useState(false);
  const [checkedKeys, setCheckedKeys] = useState(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [addCustomerOpen, setAddCustomerOpen] = useState(false);
  const [addJobOpen, setAddJobOpen] = useState(false);

  // Build a booking-shaped object from a job + the customer info
  const jobToBooking = (job) => {
    if (!detail) return job;
    return {
      ...job,
      name: detail.name,
      phone: detail.phone,
      email: detail.email,
    };
  };

  const openEditJob = (job) => {
    setEditJob(jobToBooking(job));
    setEditOpen(true);
  };

  const handleJobUpdated = (updated) => {
    // Keep the modal-target state in sync so an open modal reflects the change
    setEditJob((cur) => (cur && cur.id === updated.id ? { ...cur, ...updated } : cur));
    setCompleteJob((cur) => (cur && cur.id === updated.id ? { ...cur, ...updated } : cur));
    // Refresh selected customer detail to pick up the new state of the job
    if (selected) {
      authAxios.get(`/customers/${encodeURIComponent(selected.key)}`).then((r) => setDetail(r.data));
    }
    // Also refresh the customers list so last_status / job_count stays accurate
    load();
  };

  const handleJobDeleted = () => {
    if (selected) {
      authAxios.get(`/customers/${encodeURIComponent(selected.key)}`).then((r) => setDetail(r.data));
    }
    load();
  };

  const handleCustomerSaved = (updated) => {
    // Phone may have changed → key changes too
    setSelected(updated);
    setDetail(updated);
    load();
  };

  const handleCustomerDelete = async () => {
    if (!selected) return;
    setCustomerDeleting(true);
    try {
      const r = await authAxios.delete(`/customers/${encodeURIComponent(selected.key)}`);
      toast({
        title: "Customer deleted",
        description: `Removed ${r.data.deleted_bookings} booking${r.data.deleted_bookings === 1 ? "" : "s"}`,
      });
      setCustomerDeleteOpen(false);
      setSelected(null);
      setDetail(null);
      load();
    } catch (e) {
      toast({
        title: "Delete failed",
        description: e?.response?.data?.detail || "Server error",
        variant: "destructive",
      });
    } finally {
      setCustomerDeleting(false);
    }
  };

  const toggleChecked = (key) => {
    setCheckedKeys((s) => {
      const n = new Set(s);
      if (n.has(key)) n.delete(key);
      else n.add(key);
      return n;
    });
  };

  const clearSelection = () => setCheckedKeys(new Set());

  const doBulkDelete = async () => {
    const keys = Array.from(checkedKeys);
    if (!keys.length) return;
    setBulkDeleting(true);
    try {
      const r = await authAxios.post("/customers/bulk-delete", { keys });
      toast({
        title: `Deleted ${r.data.deleted_customers} customer${r.data.deleted_customers === 1 ? "" : "s"}`,
        description: `${r.data.deleted_bookings} booking${r.data.deleted_bookings === 1 ? "" : "s"} removed`,
      });
      setCustomers((arr) => arr.filter((c) => !checkedKeys.has(c.key)));
      if (selected && checkedKeys.has(selected.key)) {
        setSelected(null);
        setDetail(null);
      }
      clearSelection();
      setBulkDeleteOpen(false);
    } catch (e) {
      toast({
        title: "Bulk delete failed",
        description: e?.response?.data?.detail || "Server error",
        variant: "destructive",
      });
    } finally {
      setBulkDeleting(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await authAxios.get("/customers");
      setCustomers(r.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selected) {
      setDetail(null);
      return;
    }
    const fetchDetail = async () => {
      setDetailLoading(true);
      try {
        const r = await authAxios.get(`/customers/${encodeURIComponent(selected.key)}`);
        setDetail(r.data);
      } finally {
        setDetailLoading(false);
      }
    };
    fetchDetail();
  }, [selected, authAxios]);

  const filtered = useMemo(() => {
    if (!query.trim()) return customers;
    const q = query.toLowerCase();
    return customers.filter((c) =>
      [c.name, c.phone, c.email, c.address, c.suburb]
        .filter(Boolean)
        .some((s) => s.toLowerCase().includes(q))
    );
  }, [customers, query]);

  return (
    <div className="p-8" data-testid="admin-customers-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900">Customers</h1>
          <p className="text-sm text-slate-500">
            {customers.length} customer{customers.length === 1 ? "" : "s"} from {" "}
            {customers.reduce((n, c) => n + (c.job_count || 0), 0)} jobs
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name, phone, email, suburb…"
              className="pl-9 w-72"
              data-testid="customer-search"
            />
          </div>
          <Button
            onClick={() => setAddCustomerOpen(true)}
            className="bg-sky-600 hover:bg-sky-700 text-white"
            data-testid="add-customer-btn"
          >
            <Plus size={14} className="mr-1" /> Add customer
          </Button>
          <Button variant="outline" onClick={load}>Refresh</Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500">
          <Loader2 className="animate-spin" size={16} /> Loading…
        </div>
      ) : (
        <div className="grid lg:grid-cols-[1fr_540px] gap-6">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between gap-3">
              <label className="flex items-center gap-2 text-sm font-semibold text-slate-600 cursor-pointer select-none">
                <Checkbox
                  checked={
                    filtered.length > 0 && filtered.every((c) => checkedKeys.has(c.key))
                  }
                  onCheckedChange={() => {
                    setCheckedKeys((s) => {
                      const n = new Set(s);
                      const allChecked = filtered.every((c) => n.has(c.key));
                      if (allChecked) filtered.forEach((c) => n.delete(c.key));
                      else filtered.forEach((c) => n.add(c.key));
                      return n;
                    });
                  }}
                  data-testid="select-all-customers"
                  aria-label="Select all customers"
                />
                {checkedKeys.size > 0 ? (
                  <span className="text-sky-700">{checkedKeys.size} selected</span>
                ) : (
                  <span>{filtered.length} matching</span>
                )}
              </label>
              {checkedKeys.size > 0 && (
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={clearSelection} data-testid="clear-customer-selection">
                    Clear
                  </Button>
                  <Button
                    size="sm"
                    className="bg-rose-600 hover:bg-rose-700 text-white"
                    onClick={() => setBulkDeleteOpen(true)}
                    data-testid="bulk-delete-customers-btn"
                  >
                    <Trash2 size={14} className="mr-1" /> Delete {checkedKeys.size}
                  </Button>
                </div>
              )}
            </div>
            <div className="divide-y divide-slate-100 max-h-[75vh] overflow-y-auto">
              {filtered.map((c) => {
                const isChecked = checkedKeys.has(c.key);
                return (
                  <div
                    key={c.key}
                    className={`flex items-stretch hover:bg-slate-50 transition-colors ${
                      selected?.key === c.key ? "bg-sky-50" : ""
                    } ${isChecked ? "bg-rose-50/40" : ""}`}
                  >
                    <label
                      className="flex items-center pl-4 pr-2 cursor-pointer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Checkbox
                        checked={isChecked}
                        onCheckedChange={() => toggleChecked(c.key)}
                        data-testid={`select-customer-${c.key}`}
                        aria-label={`Select ${c.name}`}
                      />
                    </label>
                    <button
                      onClick={() => setSelected(c)}
                      className="flex-1 text-left pl-1 pr-5 py-4"
                      data-testid={`customer-row-${c.key}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-bold text-slate-800 flex items-center gap-1.5">
                          <User size={14} className="text-sky-600" />
                          {c.name || "(no name)"}
                        </span>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-sky-100 text-sky-800 font-semibold">
                          {c.job_count} job{c.job_count === 1 ? "" : "s"}
                        </span>
                      </div>
                      <div className="text-xs text-slate-500 flex flex-wrap gap-x-3 gap-y-0.5">
                        {c.phone && <span><Phone size={11} className="inline mr-1" />{c.phone}</span>}
                        {c.email && <span><Mail size={11} className="inline mr-1" />{c.email}</span>}
                      </div>
                      <div className="text-xs text-slate-400 mt-1">
                        Last job: {fmtDate(c.last_job_at)} · {c.last_status}
                      </div>
                    </button>
                  </div>
                );
              })}
              {filtered.length === 0 && (
                <div className="px-5 py-10 text-sm text-slate-500 text-center">
                  No customers match.
                </div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 h-fit">
            {!selected ? (
              <div className="text-sm text-slate-500 py-10 text-center">
                Select a customer to view their job history.
              </div>
            ) : detailLoading || !detail ? (
              <div className="flex items-center gap-2 text-slate-500 py-10 justify-center">
                <Loader2 className="animate-spin" size={16} /> Loading…
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-xs text-slate-500 uppercase tracking-wider mb-0.5">Customer</div>
                    <div className="text-xl font-extrabold text-slate-900">{detail.name || "(no name)"}</div>
                    <div className="text-xs text-slate-500 mt-1">
                      {detail.job_count} job{detail.job_count === 1 ? "" : "s"} ·
                      {" "}First: {fmtDate(detail.first_job_at)} ·
                      {" "}Last: {fmtDate(detail.last_job_at)}
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-slate-300 text-slate-700 hover:bg-slate-50"
                      onClick={() => setCustomerEditOpen(true)}
                      data-testid="edit-customer-btn"
                    >
                      <Pencil size={14} className="mr-1" /> Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-rose-300 text-rose-700 hover:bg-rose-50"
                      onClick={() => setCustomerDeleteOpen(true)}
                      data-testid="delete-customer-btn"
                    >
                      <Trash2 size={14} className="mr-1" /> Delete
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-2 text-sm">
                  {detail.phone && <Info icon={Phone} label="Phone" value={detail.phone} />}
                  {detail.email && <Info icon={Mail} label="Email" value={detail.email} />}
                  {(detail.address || detail.suburb) && (
                    <Info
                      icon={MapPin}
                      label="Address"
                      value={[detail.address, detail.suburb].filter(Boolean).join(", ")}
                    />
                  )}
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs text-slate-500 uppercase tracking-wider">Job history</div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-sky-300 text-sky-700 hover:bg-sky-50 h-7 text-xs"
                      onClick={() => setAddJobOpen(true)}
                      data-testid="add-job-btn"
                    >
                      <Plus size={12} className="mr-1" /> Add job
                    </Button>
                  </div>
                  <ol className="space-y-2 border-l-2 border-slate-200 pl-4">
                    {detail.jobs.map((j) => (
                      <li key={j.id} className="relative" data-testid={`job-${j.id}`}>
                        <span className="absolute -left-[22px] top-1 w-3 h-3 rounded-full bg-sky-500 border-2 border-white shadow" />
                        <div
                          role="button"
                          tabIndex={0}
                          onClick={() => openEditJob(j)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") openEditJob(j);
                          }}
                          className="w-full text-left bg-slate-50 hover:bg-sky-50 rounded-lg p-3 transition-colors border border-transparent hover:border-sky-200 cursor-pointer"
                          data-testid={`job-open-${j.id}`}
                        >
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <span className="font-bold text-slate-800 text-sm">
                              {j.service_title || "Service"}
                            </span>
                            <span className={`text-[10px] px-2 py-0.5 rounded-full ${STATUS_STYLES[j.status] || "bg-slate-100 text-slate-700"}`}>
                              {j.status}
                            </span>
                          </div>
                          <div className="text-xs text-slate-600 flex items-center gap-1.5 mb-1">
                            <CalendarDays size={11} className="text-sky-600" />
                            {j.date} {j.time && `at ${j.time}`}{j.time_to && ` - ${j.time_to}`}
                          </div>
                          <div className="text-[11px] text-slate-500">
                            Ref: <span className="font-mono">{j.reference}</span>
                            {j.address && <> · {j.address}, {j.suburb}</>}
                          </div>
                          {j.notes && (
                            <div className="mt-2 text-xs text-slate-700 bg-white rounded p-2 border border-slate-200 flex items-start gap-1.5">
                              <FileText size={11} className="text-slate-400 mt-0.5 shrink-0" />
                              <span className="whitespace-pre-line">{j.notes}</span>
                            </div>
                          )}
                          {j.image_count > 0 && (
                            <div className="mt-2 flex items-center gap-1 text-[11px] text-slate-500">
                              <ImageIcon size={11} /> {j.image_count} photo{j.image_count === 1 ? "" : "s"}
                              <div className="flex gap-1 ml-2">
                                {j.image_urls.slice(0, 4).map((u) => (
                                  <a key={u} href={u} target="_blank" rel="noreferrer"
                                     onClick={(e) => e.stopPropagation()}
                                     className="w-7 h-7 rounded overflow-hidden border border-slate-200">
                                    <img src={u} alt="" className="w-full h-full object-cover" />
                                  </a>
                                ))}
                              </div>
                            </div>
                          )}
                          {j.status !== "completed" && j.status !== "cancelled" && j.status !== "declined" && (
                            <div className="mt-2 flex justify-end">
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-slate-300 text-slate-700 hover:bg-slate-100 h-7 text-xs"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setCompleteJob(jobToBooking(j));
                                  setCompleteOpen(true);
                                }}
                                data-testid={`job-complete-${j.id}`}
                              >
                                Mark complete
                              </Button>
                            </div>
                          )}
                        </div>
                      </li>
                    ))}
                    {detail.jobs.length === 0 && (
                      <li className="text-xs text-slate-500">No jobs yet.</li>
                    )}
                  </ol>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <BookingEditModal
        open={editOpen}
        onOpenChange={setEditOpen}
        booking={editJob}
        onSaved={handleJobUpdated}
        onDeleted={handleJobDeleted}
      />

      <BookingActionModal
        open={completeOpen}
        action="complete"
        booking={completeJob}
        onOpenChange={setCompleteOpen}
        onCompleted={handleJobUpdated}
      />

      <CustomerEditModal
        open={customerEditOpen}
        onOpenChange={setCustomerEditOpen}
        customer={detail}
        onSaved={handleCustomerSaved}
      />

      <JobCreateModal
        open={addCustomerOpen}
        onOpenChange={setAddCustomerOpen}
        customer={null}
        lockCustomer={false}
        onCreated={(newBooking) => {
          load();
          // Surface the new customer in the right panel
          const key = newBooking.phone ? `phone:${newBooking.phone}` : `email:${newBooking.email}`;
          setSelected({ key, name: newBooking.name, phone: newBooking.phone, email: newBooking.email });
        }}
      />

      <JobCreateModal
        open={addJobOpen}
        onOpenChange={setAddJobOpen}
        customer={detail}
        lockCustomer={true}
        onCreated={() => {
          if (selected) {
            authAxios
              .get(`/customers/${encodeURIComponent(selected.key)}`)
              .then((r) => setDetail(r.data));
          }
          load();
        }}
      />

      <AlertDialog open={customerDeleteOpen} onOpenChange={setCustomerDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete customer?</AlertDialogTitle>
            <AlertDialogDescription>
              {detail ? `This will delete ${detail.name || "this customer"} and all their ${detail.job_count} booking${detail.job_count === 1 ? "" : "s"}.` : ""}
              {" "}The customer is NOT notified. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={customerDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCustomerDelete}
              disabled={customerDeleting}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="confirm-customer-delete-btn"
            >
              {customerDeleting && <Loader2 className="animate-spin mr-2" size={14} />}
              Delete customer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {checkedKeys.size} customer{checkedKeys.size === 1 ? "" : "s"}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the selected customers and all their bookings.
              Customers are NOT notified. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={doBulkDelete}
              disabled={bulkDeleting}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="confirm-bulk-delete-customers-btn"
            >
              {bulkDeleting && <Loader2 className="animate-spin mr-2" size={14} />}
              Delete {checkedKeys.size}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Info({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <Icon size={14} className="text-sky-600 mt-0.5" />
      <div>
        <div className="text-[11px] text-slate-500 uppercase tracking-wider">{label}</div>
        <div className="text-sm font-medium text-slate-800 break-all">{value}</div>
      </div>
    </div>
  );
}
